/*
 * ATTORNEY REVIEW PENDING. This page is LIVE; the content below was reconciled
 * to the verified tracker inventory and is accurate, but the OPEN QUESTIONS FOR
 * COUNSEL listed below remain to be confirmed/finalized by outside counsel.
 *
 * This file was reconciled to the VERIFIED tracker inventory of the public site
 * (see epic GuardlyApp/Guardly-Web-App#51, WS-3 #54). The previous version
 * documented many cookies and third parties that the site does NOT use
 * (csrf_token, session_id, user_preferences, language, timezone, recent_searches,
 * Google Analytics, Hotjar, New Relic, Facebook Pixel, Google Ads, LinkedIn
 * Insight, Stripe, PayPal, Intercom, Zendesk, Cloudflare, Google Maps, social
 * logins) and omitted the only real storage the site uses (three first-party
 * localStorage keys). Over- and under-disclosure are both compliance risks.
 *
 * ACTUAL public-site storage (verified in source):
 *   - localStorage "token"            — auth/session (JWT) for signed-in users
 *   - localStorage "user"             — cached account profile to keep you signed in
 *   - localStorage "guardly_device_id"— random device id for push-notification registration
 *   - localStorage "guardly_apply_draft_v1" — saved progress on the /apply form
 *   - localStorage "guardly_ref_v1"   — referral code from a ?ref= link (90 days)
 *   - localStorage "guardly_lang_v1"  — the language chosen for the /apply form
 *   All first-party; transmitted only to api.guardly.app. No HTTP tracking
 *   cookies, no analytics/advertising/social/session-replay tools are in use.
 *
 * INVENTORY UPDATE (multilingual /apply rollout): "guardly_lang_v1" is new with
 * that change. Re-verifying the inventory at the same time surfaced that
 * "guardly_apply_draft_v1" and "guardly_ref_v1" were ALREADY in use and had
 * never been disclosed here — they are added above and in the table below.
 * All three are functional/strictly-necessary preference storage, set
 * first-party, never transmitted to a third party.
 *
 * OPEN QUESTIONS FOR COUNSEL (see PR description):
 *   - Retention periods to state (DynamoDB/S3 onboarding data) — left descriptive.
 *   - Whether guardly_device_id counts as "strictly necessary" for disclosure.
 *   - Whether the three newly listed keys are correctly classed as strictly
 *     necessary (draft/referral/language), or need consent gating.
 *   - Whether a Data Protection Officer (dpo@guardly.app) actually exists / is required.
 *   - Confirm/adjust the Effective Date below (currently set to the publication date).
 */
import React from 'react';
import { Link } from 'react-router-dom';

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-10 pb-6 border-b-2 border-gray-200">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Cookie &amp; Local Storage Policy</h1>
          <p className="text-gray-600 italic">Effective Date: June 30, 2026</p>
        </header>

        <main className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              Introduction
            </h2>
            <p className="text-gray-700 leading-relaxed">
              GUARDLY ("we," "our," or "us") uses a small number of strictly necessary browser storage
              technologies to operate this website and our onboarding portal. This policy explains what we
              store on your device, why, and your choices regarding non-essential technologies.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              We do <strong>not</strong> use analytics, advertising, social media, or other non-essential
              tracking technologies on this website at this time. If we introduce any in the future, we will
              ask for your prior opt-in consent before they are activated (see "Your Choices" below).
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              This policy should be read alongside our{' '}
              <Link to="/privacy" className="text-primary-600 hover:text-primary-700 font-medium">Privacy Policy</Link> and{' '}
              our <Link to="/terms" className="text-primary-600 hover:text-primary-700 font-medium">Terms of Service</Link>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              1. Cookies and Similar Technologies
            </h2>
            <p className="text-gray-700 leading-relaxed">
              "Cookies" are small text files stored on your device by a website. Related technologies include
              browser <strong>local storage</strong>, which lets a site save information in your browser.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              This website relies primarily on <strong>local storage</strong> rather than traditional cookies.
              We do not currently use web beacons, pixel tags, or session-replay technologies.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              2. What We Store (Strictly Necessary)
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The following items are <strong>strictly necessary</strong> to provide features you request,
              such as signing in and staying signed in. They are set by GUARDLY (first-party), stored in your
              browser's local storage, and transmitted only to GUARDLY's own servers (api.guardly.app). They
              are not shared with third parties and are not used for advertising or cross-site tracking.
              Because they are strictly necessary, they cannot be switched off without breaking core features.
            </p>
            <div className="overflow-x-auto mt-4">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-primary-600">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Name</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Type</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Purpose</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Retention</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-700">token</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Local storage</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Authentication and session management for signed-in users</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Until you sign out or the session expires</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-700">user</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Local storage</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Caches your basic account profile so you stay signed in</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Until you sign out</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-700">guardly_device_id</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Local storage</td>
                    <td className="px-6 py-4 text-sm text-gray-700">A randomly generated identifier used to register your device for push notifications</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Until you clear your browser storage</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-700">guardly_apply_draft_v1</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Local storage</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Saves your progress on the Security Service Provider application so you can finish it later. Your date of birth and any criminal-record details are deliberately excluded, and uploaded files are never stored</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Until you submit the application or clear your browser storage</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-700">guardly_ref_v1</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Local storage</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Remembers the referral code you arrived with, so the Security Service Provider who referred you is credited when you apply</td>
                    <td className="px-6 py-4 text-sm text-gray-700">90 days</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-700">guardly_lang_v1</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Local storage</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Remembers the language you chose for the application form so you do not have to re-select it on every visit</td>
                    <td className="px-6 py-4 text-sm text-gray-700">Until you clear your browser storage</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-gray-700 leading-relaxed mt-4">
              You can delete these at any time by clearing your browser's storage for this site; doing so will
              sign you out and remove the device identifier.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              3. Non-Essential Cookies (None Currently in Use)
            </h2>
            <p className="text-gray-700 leading-relaxed">
              We do not currently use any analytics, performance, advertising, marketing, social media, or
              other non-essential cookies or tracking technologies on this website. As a result, there are no
              non-essential third-party tags loading on the site, and there is currently nothing to opt out of
              in those categories.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              If we add any such technology in the future, we will update this policy and obtain your prior
              opt-in consent before it is activated.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              4. Third-Party Services
            </h2>
            <p className="text-gray-700 leading-relaxed">
              This website is hosted on Amazon Web Services (Amazon CloudFront and Amazon S3), and our
              application interfaces communicate with our own backend at api.guardly.app, also hosted on AWS.
              These are service providers that process data on our behalf to deliver the site; they are not
              used to track you across other websites and do not set advertising cookies through this site.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              We do not currently embed third-party advertising, analytics, social media, chat, or mapping
              tools on this website.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              5. Your Choices
            </h2>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">5.1 Consent for Non-Essential Technologies</h3>
            <p className="text-gray-700 leading-relaxed">
              We use an opt-in model for any non-essential technologies: they will not load or transmit data
              until you have given consent. Strictly necessary storage (described in Section 2) does not
              require consent because the site cannot function without it. When non-essential technologies are
              offered, you will be able to review and change your choices at any time using the cookie settings
              control on this page and in our website footer.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">5.2 Browser Controls</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              You can also control or delete cookies and local storage through your browser settings:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li><a href="https://support.google.com/chrome/answer/95647" className="text-primary-600 hover:text-primary-700" target="_blank" rel="noopener noreferrer">Chrome</a></li>
              <li><a href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences" className="text-primary-600 hover:text-primary-700" target="_blank" rel="noopener noreferrer">Firefox</a></li>
              <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" className="text-primary-600 hover:text-primary-700" target="_blank" rel="noopener noreferrer">Safari</a></li>
              <li><a href="https://support.microsoft.com/en-us/windows/manage-cookies-in-microsoft-edge-168dab11-0753-043d-7c16-ede5947fc64d" className="text-primary-600 hover:text-primary-700" target="_blank" rel="noopener noreferrer">Edge</a></li>
            </ul>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-6">
              <p className="text-gray-700">
                <strong>Note:</strong> Clearing strictly necessary storage will sign you out and may interrupt
                core features such as signing in and registering your device for notifications.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              6. Global Privacy Control and Do Not Track
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Some browsers send a Global Privacy Control (GPC) or "Do Not Track" (DNT) signal indicating that
              you do not want to be tracked. Because we do not currently use non-essential tracking
              technologies, there is no such tracking to disable. If we introduce non-essential technologies in
              the future, we will treat a GPC or DNT signal as a valid request to opt out and will not activate
              those technologies for your browser.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              7. Children's Privacy
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Our services are not intended for children under 18. We do not knowingly collect personal
              information from children. If you believe we have inadvertently done so, please contact us.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              8. Where Your Data Is Processed
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Our infrastructure is operated on Amazon Web Services in the United States. If you access the site
              from outside the United States, information necessary to provide the site may be processed in the
              United States.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              9. Updates to This Policy
            </h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this policy to reflect changes in our practices or for legal reasons. We will post
              the updated policy here and revise the "Effective Date" above. We review this policy at least
              annually and whenever we add a new tracking, analytics, chat, advertising, form, or similar
              data-collection tool.
            </p>
          </section>

          <section className="mb-12 bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              10. Contact Us
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have questions about this policy, please contact us:
            </p>
            {/* COUNSEL: confirm which mailboxes are monitored and whether a DPO is appointed/required. */}
            <address className="not-italic space-y-2">
              <p className="text-gray-700">
                <strong>Email:</strong> <a href="mailto:privacy@guardly.app" className="text-primary-600 hover:text-primary-700">privacy@guardly.app</a>
              </p>
              <p className="text-gray-700">
                <strong>General Inquiries:</strong> <a href="mailto:contact@guardly.app" className="text-primary-600 hover:text-primary-700">contact@guardly.app</a>
              </p>
              <p className="text-gray-700">
                <strong>Website:</strong> <a href="https://www.guardly.app" className="text-primary-600 hover:text-primary-700">www.guardly.app</a>
              </p>
            </address>
          </section>

          <section className="mb-12 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Cookie Settings</h2>
            <p className="text-gray-700 mb-4">
              Manage your preferences for non-essential technologies at any time:
            </p>
            {/* WS-2 (#53) wires this to the consent manager's preferences panel. Until then it
                accurately reports that there are no non-essential technologies to configure. */}
            <button
              onClick={() => {
                if (typeof window !== 'undefined' && window.GuardlyConsent && typeof window.GuardlyConsent.openPreferences === 'function') {
                  window.GuardlyConsent.openPreferences();
                } else {
                  alert('This website does not currently use any non-essential cookies or tracking technologies, so there are no preferences to configure. Strictly necessary storage required to run the site cannot be disabled here.');
                }
              }}
              className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium"
            >
              Manage Cookie Preferences
            </button>
          </section>
        </main>
      </div>
    </div>
  );
};

export default CookiePolicy;
